import React, { useEffect, useMemo, useState } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import PortalModuleCard from '../components/portal/PortalModuleCard';
import PortalTodoCard from '../components/portal/PortalTodoCard';
import PortalShortcutCard from '../components/portal/PortalShortcutCard';
import authService from '../services/authService';
import klubService from '../services/klubService';
import menuVisibilityService from '../services/menuVisibilityService';
import { PORTAL_MODULES, ROUTES } from '../config/routes';
import { PORTAL_MODULE_MENU_KEYS } from '../config/menuCatalog';
import {
  getHomeSubtitle,
  getVisibleHomeShortcuts,
  getVisibleHomeTodos,
  homeShowsKlubBlocks,
  resolveHomeRoles,
} from '../config/homeShortcuts';
import useHomeActionCounts from '../hooks/useHomeActionCounts';

const PortalHomePage = () => {
  const [user, setUser] = useState(null);
  const [menuTick, setMenuTick] = useState(0);
  const { depotPending, pubGraphiste } = useHomeActionCounts();
  const [integrationCount, setIntegrationCount] = useState(0);
  const [klubLoaded, setKlubLoaded] = useState(false);

  useEffect(() => {
    setUser(authService.getCurrentUser());
    let cancelled = false;
    menuVisibilityService.load()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setMenuTick((t) => t + 1);
      });
    return () => { cancelled = true; };
  }, []);

  const homeRoles = useMemo(() => {
    void menuTick;
    return resolveHomeRoles();
  }, [menuTick]);

  useEffect(() => {
    if (!menuVisibilityService.canSee('section.klub') && !menuVisibilityService.canSee('menu.klub')) {
      setIntegrationCount(0);
      setKlubLoaded(false);
      return undefined;
    }
    let cancelled = false;
    klubService.listAdherents({ lifecycle_phase: 'integration', limit: 1 })
      .then((data) => {
        if (!cancelled) {
          setIntegrationCount(data.pagination?.total ?? data.adherents?.length ?? 0);
          setKlubLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIntegrationCount(0);
          setKlubLoaded(true);
        }
      });
    return () => { cancelled = true; };
  }, [menuTick, homeRoles]);

  const canSeeKlub = menuTick > 0 && (
    menuVisibilityService.canSee('section.klub') || menuVisibilityService.canSee('menu.klub')
  );

  const greeting = user?.firstName
    ? `Bonjour ${user.firstName}`
    : 'Bonjour';

  const subtitle = getHomeSubtitle(homeRoles);

  const countsByKey = {
    depotPending,
    pubGraphiste,
  };

  const todoItems = useMemo(() => {
    void menuTick;
    return getVisibleHomeTodos(
      homeRoles,
      (key) => menuVisibilityService.canSee(key),
      countsByKey,
    );
    // counts inclus via deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuTick, homeRoles, depotPending, pubGraphiste]);

  const shortcuts = useMemo(() => {
    void menuTick;
    return getVisibleHomeShortcuts(homeRoles, (key) => menuVisibilityService.canSee(key));
  }, [menuTick, homeRoles]);

  const visibleModules = useMemo(() => {
    void menuTick;
    return PORTAL_MODULES.filter((module) => {
      const key = PORTAL_MODULE_MENU_KEYS[module.id];
      return !key || menuVisibilityService.canSee(key);
    });
  }, [menuTick]);

  return (
    <MasterLayout>
      <div className="portal-home">
        <div className="portal-home__header mb-32">
          <h1 className="portal-home__title">{greeting}</h1>
          <p className="portal-home__subtitle mb-0">{subtitle}</p>
        </div>

        {todoItems.length > 0 && (
          <section className="mb-32">
            <h2 className="portal-home__section-title">À traiter</h2>
            <div className="row g-3">
              {todoItems.map((item) => (
                <div key={item.id} className="col-12 col-md-6 d-flex">
                  <PortalTodoCard
                    item={item}
                    count={countsByKey[item.countKey] || 0}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {canSeeKlub && klubLoaded && (
          <section className="mb-32">
            <div className="row g-3">
              <div className="col-12 col-sm-6 col-xl-4 d-flex">
                <PortalShortcutCard
                  shortcut={{
                    to: `${ROUTES.klub.dashboard}?vue=integration`,
                    icon: 'mdi:progress-clock',
                    label: `${integrationCount} site${integrationCount === 1 ? '' : 's'} Klub en cours`,
                    description: 'Sites en cours d’intégration',
                  }}
                />
              </div>
              <div className="col-12 col-sm-6 col-xl-4 d-flex">
                <PortalShortcutCard
                  shortcut={{
                    to: `${ROUTES.klub.dashboard}?vue=actifs&etape=depasse`,
                    icon: 'mdi:calendar-alert',
                    label: 'Liste éligible Avantage anniv klub',
                    description: 'Date de préavis dépassée',
                  }}
                />
              </div>
            </div>
          </section>
        )}

        {shortcuts.length > 0 && (
          <section className="mb-32">
            <h2 className="portal-home__section-title">Raccourcis</h2>
            <div className="row g-3">
              {shortcuts.map((shortcut) => (
                <div key={shortcut.id} className="col-12 col-sm-6 col-xl-4 d-flex">
                  <PortalShortcutCard shortcut={shortcut} />
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="portal-home__section-title">Modules</h2>
          <div className="row g-3">
            {visibleModules.map((module) => (
              <div key={module.id} className="col-12 col-md-6 col-xl-3 d-flex">
                <PortalModuleCard module={module} />
              </div>
            ))}
            {visibleModules.length === 0 && (
              <div className="col-12">
                <p className="text-secondary-light mb-0">
                  Aucun module accessible avec votre rôle.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </MasterLayout>
  );
};

export default PortalHomePage;
